/**
 * Text search helpers - accent-insensitive matching and in-page highlighting
 * Used by the guidebook search (GuideBookSearchService, highlightedHtml binding)
 */

// Letters that don't decompose into base letter + accent under Unicode NFD
const SPECIAL_FOLDS = { 'ł': 'l', 'Ł': 'l', 'ß': 'ss', 'ø': 'o', 'Ø': 'o', 'đ': 'd', 'Đ': 'd' };

/**
 * Fold a single character: lowercase, accents removed (ż -> z, ł -> l)
 * @param {string} ch - One character
 * @returns {string} Folded text (may be empty or longer than one character)
 */
function foldChar(ch) {
    if (SPECIAL_FOLDS[ch]) return SPECIAL_FOLDS[ch];
    return ch.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
}

/**
 * Fold text for comparison, so "zolty" matches "Żółty"
 * @param {string} text - Text to fold
 * @returns {string} Lowercase text without accents
 */
export function foldText(text) {
    if (!text) return '';
    let out = '';
    for (const ch of String(text)) out += foldChar(ch);
    return out;
}

/**
 * Fold text and keep a map from each folded character back to its index in the original,
 * so a match found in the folded text can be located in the original text
 * @private
 */
function foldWithMap(text) {
    let folded = '';
    const map = [];
    let index = 0;
    for (const ch of text) {
        const f = foldChar(ch);
        for (let i = 0; i < f.length; i++) map.push(index);
        folded += f;
        index += ch.length;
    }
    return { folded, map };
}

/**
 * Wrap every match of the query inside an element's text in <mark> elements.
 * Works on text nodes only: tags, attributes and links are left untouched, and the
 * query is never inserted as HTML.
 *
 * @param {HTMLElement} root - Element whose text to search
 * @param {string} query - Search text
 * @param {number} [minLength=3] - Shorter queries are ignored
 * @returns {HTMLElement|null} The first <mark> added, or null if nothing matched
 */
export function highlightMatches(root, query, minLength = 3) {
    const term = foldText((query || '').trim());
    if (!root || term.length < minLength) return null;

    const textNodes = [];
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
    while (walker.nextNode()) textNodes.push(walker.currentNode);

    let firstMark = null;
    for (const node of textNodes) {
        const text = node.nodeValue;
        const { folded, map } = foldWithMap(text);
        let pos = folded.indexOf(term);
        if (pos < 0) continue;

        const fragment = document.createDocumentFragment();
        let last = 0;
        while (pos >= 0) {
            const start = map[pos];
            const endIndex = pos + term.length - 1;
            const end = map[endIndex] + (text.codePointAt(map[endIndex]) > 0xffff ? 2 : 1);
            if (start >= last) {
                fragment.append(text.slice(last, start));
                const mark = document.createElement('mark');
                mark.textContent = text.slice(start, end);
                mark.style.cssText = 'background:#fde68a; color:inherit; border-radius:2px; padding:0 1px;';
                fragment.append(mark);
                if (!firstMark) firstMark = mark;
                last = end;
            }
            pos = folded.indexOf(term, pos + term.length);
        }
        fragment.append(text.slice(last));
        node.replaceWith(fragment);
    }
    return firstMark;
}
